export type HealthIconStyle =
  | 'outline'
  | 'filled'
  | 'outline-24px'
  | 'filled-24px';

export type HealthIconCategory =
  | 'blood'
  | 'body'
  | 'conditions'
  | 'contraceptives'
  | 'devices'
  | 'diagnostics'
  | 'emotions'
  | 'exercise'
  | 'graphs'
  | 'medications'
  | 'nutrition'
  | 'objects'
  | 'people'
  | 'places'
  | 'ppe'
  | 'shapes'
  | 'specialties'
  | 'symbols'
  | 'typography'
  | 'vehicles'
  | 'zoonoses';

export interface ParsedHealthIcon {
  style: HealthIconStyle;
  category: string;
  name: string;
}

export const HEALTH_ICON_STYLES: readonly HealthIconStyle[] = [
  'outline',
  'filled',
  'outline-24px',
  'filled-24px',
] as const;

export const HEALTH_ICON_CATEGORIES: readonly HealthIconCategory[] = [
  'blood',
  'body',
  'conditions',
  'contraceptives',
  'devices',
  'diagnostics',
  'emotions',
  'exercise',
  'graphs',
  'medications',
  'nutrition',
  'objects',
  'people',
  'places',
  'ppe',
  'shapes',
  'specialties',
  'symbols',
  'typography',
  'vehicles',
  'zoonoses',
] as const;

/**
 * Parses an icon identifier into style, category, and icon name.
 * Supported formats:
 * - "blood/blood_bag" (defaults to outline)
 * - "health:blood/blood_bag"
 * - "filled/blood/blood_bag"
 * - "health:filled/blood/blood_bag"
 * - "health:outline-24px/devices/stethoscope"
 */
export function parseHealthIconKey(
  iconKey: string,
  defaultStyle: HealthIconStyle = 'outline'
): ParsedHealthIcon {
  if (!iconKey) {
    return { style: defaultStyle, category: '', name: '' };
  }

  let cleanKey = iconKey.trim();
  if (cleanKey.startsWith('health:')) {
    cleanKey = cleanKey.slice('health:'.length);
  } else if (cleanKey.startsWith('healthicons:')) {
    cleanKey = cleanKey.slice('healthicons:'.length);
  }

  if (cleanKey.endsWith('.svg')) {
    cleanKey = cleanKey.slice(0, -4);
  }

  const parts = cleanKey.split('/').map((p) => p.trim()).filter(Boolean);

  if (parts.length >= 3) {
    const maybeStyle = parts[0] as HealthIconStyle;
    if (HEALTH_ICON_STYLES.includes(maybeStyle)) {
      return {
        style: maybeStyle,
        category: parts[1],
        name: parts.slice(2).join('/'),
      };
    }
  }

  if (parts.length === 2) {
    const maybeStyle = parts[0] as HealthIconStyle;
    if (HEALTH_ICON_STYLES.includes(maybeStyle)) {
      return {
        style: maybeStyle,
        category: '',
        name: parts[1],
      };
    }
    return {
      style: defaultStyle,
      category: parts[0],
      name: parts[1],
    };
  }

  return {
    style: defaultStyle,
    category: '',
    name: cleanKey,
  };
}

/**
 * Builds the URL path to the static SVG asset served by Angular.
 */
export function resolveHealthIconAssetPath(
  iconKey: string,
  overrideStyle?: HealthIconStyle
): string {
  const parsed = parseHealthIconKey(iconKey, overrideStyle ?? 'outline');
  const style = overrideStyle ?? parsed.style;
  const category = parsed.category;
  const name = parsed.name;

  if (category) {
    return `assets/health-icons/${style}/${category}/${name}.svg`;
  }
  return `assets/health-icons/${style}/${name}.svg`;
}
